'use client'
import { cn } from '@/lib/utils'
import { Icon } from '@/ui/components'
import { AnimatePresence, HTMLMotionProps, motion } from 'motion/react'
import * as React from 'react'

type CopyButtonProps = Omit<
  HTMLMotionProps<'button'>,
  'children' | 'onCopy'
> & {
  content?: string
  delay?: number
  /** Called after successful copy. Return false to prevent copy action. */
  onCopy?: (_content: string) => void | boolean
  isCopied?: boolean
  onCopyChange?: (_isCopied: boolean) => void
}
function CopyButton({
  content,
  className,
  delay = 3000,
  onClick,
  onCopy,
  isCopied,
  onCopyChange,
  ...props
}: CopyButtonProps) {
  const [localIsCopied, setLocalIsCopied] = React.useState(isCopied ?? false)
  const IconComp = localIsCopied ? 'material-symbols:check' : 'stash:link-solid'
  React.useEffect(() => {
    setLocalIsCopied(isCopied ?? false)
  }, [isCopied])
  const handleIsCopied = React.useCallback(
    (isCopied: boolean) => {
      setLocalIsCopied(isCopied)
      onCopyChange?.(isCopied)
    },
    [onCopyChange]
  )
  const handleCopy = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isCopied) {
        return
      }
      if (content || window.location.href) {
        // Call onCopy first to allow preventing the copy action
        const shouldCopy = onCopy?.(content || window.location.href)
        if (shouldCopy === false) {
          onClick?.(e)
          return
        }
        navigator.clipboard
          .writeText(content || window.location.href)
          .then(() => {
            handleIsCopied(true)
            setTimeout(() => handleIsCopied(false), delay)
          })
          .catch((error) => {
            console.error('Error copying command', error)
          })
      }
      onClick?.(e)
    },
    [isCopied, content, delay, onClick, onCopy, handleIsCopied]
  )
  return (
    <motion.button
      data-slot="copy-button"
      // whileHover={{ scale: 1.05 }}
      // whileTap={{ scale: 0.95 }}
      className={cn(
        'absolute inset-0 flex cursor-pointer items-center justify-center',
        className
      )}
      onClick={handleCopy}
      {...(props as any)}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={localIsCopied ? 'check' : 'copy'}
          data-slot="copy-button-icon"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0 }}
          transition={{ duration: 0.15 }}
        >
          <Icon icon={IconComp} className="size-5" />
        </motion.span>
      </AnimatePresence>
    </motion.button>
  )
}
export { CopyButton, type CopyButtonProps }
